"use client";
import { useState, useEffect } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import dynamic from "next/dynamic";

import { equalsContractNameToId } from "mock/phone-confirmation";

import ModalTextContent from "@/src/components/modal-text-content";
import Input from "components/common/input";
import CheckBoxItem from "components/common/checkbox-item";
import Dropdown from "components/common/dropdown";
import { renderContent } from "components/signup-forms/individual-form/modal-contents";

import styles from "./styles.module.css";

const Modal = dynamic(() => import("components/common/modal"), { ssr: false });

const SecondForm = ({
  errors,
  setErrors,
  form,
  setForm,
  recaptchaRef,
  cities,
  districts,
  handleInputFocus,
  isOpenAgreements,
  setIsOpenAgreements,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModalContent, setSelectedModalContent] = useState(null);

  const individualKvkkCheckboxTitleClass = classNames({
    [styles.checkContent]: true,
    [styles.noncheckContent]: errors.individualKvkk?.value,
  });

  const membershipCheckboxTitleClass = classNames({
    [styles.checkContent]: true,
    [styles.noncheckContent]: errors.membership?.value,
  });

  const errorMemberDisplayClass = classNames({
    [styles.errorCheckboxClass]: errors.membership?.value,
  });

  const errorKvkkDisplayClass = classNames({
    [styles.errorCheckboxClass]: errors.individualKvkk?.value,
  });

  const handleCompanyInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setIsOpenAgreements(true);
    setForm({
      ...form,
      companyName: value,
    });
  };

  const handleCheckApprove =
    ({ key }) =>
    (event) => {
      const {
        target: { checked },
      } = event;
      const contracts = form.contracts.reduce(
        (acc, contract) => {
          if (contract.templateId === equalsContractNameToId[key]) {
            const selectedCheckBoxIndex = acc.indexOf(contract);
            acc[selectedCheckBoxIndex].approved = checked;
          }
          return acc;
        },
        [...form.contracts]
      );

      setForm({ ...form, contracts });
      setErrors({ ...errors, [key]: { value: false, message: null } });
    };

  const handleSubscribeCheckboxToggle = (key) => (event) => {
    const {
      target: { checked },
    } = event;
    setForm({ ...form, [key]: checked, allowSms: checked });
    setErrors({ ...errors, [key]: { value: false, message: null } });
  };

  const handleModalClose = () => setIsModalOpen(false);
  const handleModalOpen = (value) => {
    setSelectedModalContent(value);
    setIsModalOpen(true);
  };

  const handleCityChange = (e) => {
    setForm({ ...form, cityId: e.key });
    setIsOpenAgreements(true);
  };

  const handleDistrictsChange = (e) => {
    setForm({ ...form, districtId: e.key });
    setIsOpenAgreements(true);
  };

  const handleLocationFocus = (type) => {
    setErrors({ ...errors, [type]: { value: false, message: null } });
  };

  useEffect(() => {
    setForm({ ...form, districtId: null });
  }, [form.cityId]);
  return (
    <div>
      <div>
        <label className={styles.inputTitle}>Firma Adı</label>
        <div className={styles.mailInput}>
          <Input
            errorMessage={errors?.companyName?.message}
            inputClass={styles.loginInput}
            type="text"
            placeholder="Firma Adınızı Giriniz"
            onChange={handleCompanyInputChange}
            onFocus={() => handleInputFocus("companyName")}
            value={form.companyName || ""}
          />
        </div>
      </div>
      <div className={styles.locationWrapper}>
        <div>
          <label className={styles.inputTitle}>İl</label>
          <div className={styles.dropdownWrapper}>
            <Dropdown
              errorMessage={errors?.cityId?.message}
              options={cities}
              placeHolder="İl"
              placeHolderClass={styles.dropdownPlaceholder}
              onChange={handleCityChange}
              listClass={styles.districtsClass}
              handleDropdownButton={() => handleLocationFocus("cityId")}
            />
          </div>
        </div>
        <div>
          <label className={styles.inputTitle}>İlçe</label>
          <div className={styles.dropdownWrapper}>
            <Dropdown
              errorMessage={errors?.districtId?.message}
              options={districts}
              placeHolder="İlçe"
              disabled={form.cityId === null}
              placeHolderClass={styles.dropdownPlaceholder}
              onChange={handleDistrictsChange}
              listClass={styles.districtsClass}
              handleDropdownButton={() => handleLocationFocus("districtId")}
              reset={form.cityId}
            />
          </div>
        </div>
      </div>
      {isOpenAgreements && (
        <div className={styles.actionWrap}>
          <div className={styles.formGroup}>
            <CheckBoxItem
              title={
                <div className={membershipCheckboxTitleClass}>
                  <span onClick={() => handleModalOpen("SIGNUP_CONTRACT")}>Üyelik Sözleşmesini</span> ve{" "}
                  <span onClick={() => handleModalOpen("SIGNUP_CONTRACT")}>gizlilik politikasını</span> okudum,
                  onaylıyorum
                </div>
              }
              name="membership"
              onChange={handleCheckApprove({ key: "membership" })}
              displayClass={errorMemberDisplayClass}
              checked={form.contracts?.find((contract) => contract.templateId === 2).approved}
            />
          </div>
          <div className={styles.formGroup}>
            <CheckBoxItem
              title={
                <div className={individualKvkkCheckboxTitleClass}>
                  Kişisel verilerin işlenmesiyle ilgili{" "}
                  <span onClick={() => handleModalOpen("KVKK")}>Aydınlatma Metni</span>
                  {`'ni okuyup anladığımı beyan ve kabul ederim.`}
                </div>
              }
              name="individualKvkk"
              onChange={handleCheckApprove({ key: "individualKvkk" })}
              displayClass={errorKvkkDisplayClass}
              checked={form.contracts?.find((contract) => contract.templateId === 4).approved}
            />
          </div>
          <div className={styles.formGroup}>
            <CheckBoxItem
              title={
                <div className={styles.checkContent}>
                  Verilerimin <span onClick={() => handleModalOpen("EXPLICIT_CONSENT")}>Açık Rıza Metni</span>
                  {`'nde belirtilen şekilde işlenmesine onay veriyorum.`}
                </div>
              }
              name="explicitConsent"
              onChange={handleCheckApprove({ key: "explicitConsent" })}
              checked={form.contracts?.find((contract) => contract.templateId === 5).approved}
            />
          </div>
          <div className={styles.formGroup}>
            <CheckBoxItem
              title={
                <div className={styles.checkContent}>
                  Tüm duyuru ve kampanyalardan ticari e-ileti yoluyla haberdar olmak istiyorum.
                </div>
              }
              name="subscribe"
              onChange={handleSubscribeCheckboxToggle("allowCommunication")}
              checked={form.allowCommunication}
            />
          </div>
          <div className={styles.formGroup}>
            <ReCAPTCHA
              key="captcha"
              ref={recaptchaRef}
              sitekey={window.env.EJ_KEYS_RECAPTCHA}
              size="invisible"
              badge="inline"
            />
          </div>
          <Modal printButton isOpen={isModalOpen} handleClose={() => handleModalClose()}>
            <ModalTextContent>{renderContent(selectedModalContent)}</ModalTextContent>
          </Modal>
        </div>
      )}
    </div>
  );
};

SecondForm.propTypes = {
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  form: PropTypes.object,
  setForm: PropTypes.func,
  recaptchaRef: PropTypes.object,
  cities: PropTypes.array,
  districts: PropTypes.array,
  handleInputFocus: PropTypes.func,
  isOpenAgreements: PropTypes.bool,
  setIsOpenAgreements: PropTypes.func,
};

export default SecondForm;
